import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
const SUBJECTS = ['Math', 'Science', 'English', 'History', 'Art', 'PE', 'Music', 'Computer', 'Other'];

interface TimetableEntry {
  id: number;
  dayOfWeek: string;
  periodNumber: number;
  subject: string;
  teacher: string | null;
  room: string | null;
  timeSlot: string | null;
}

const SUBJECT_COLORS: Record<string, string> = {
  Math: 'bg-blue-100 text-blue-700',
  Science: 'bg-green-100 text-green-700',
  English: 'bg-yellow-100 text-yellow-700',
  History: 'bg-orange-100 text-orange-700',
  Art: 'bg-pink-100 text-pink-700',
  PE: 'bg-red-100 text-red-700',
  Music: 'bg-purple-100 text-purple-700',
  Computer: 'bg-indigo-100 text-indigo-700',
  Other: 'bg-gray-100 text-gray-700',
};

const Timetable: React.FC<{ kidId?: number | string }> = ({ kidId }) => {
  const { userId } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const [entries, setEntries] = useState<TimetableEntry[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    dayOfWeek: 'Monday', periodNumber: 1, subject: 'Math',
    teacher: '', room: '', timeSlot: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTimetable(); }, []);

  const fetchTimetable = async () => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/timetable`);
      setEntries(res.data);
    } catch {}
  };

  const getEntry = (day: string, period: number) =>
    entries.find(e => e.dayOfWeek === day && e.periodNumber === period);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/api/kids/${effectiveKidId}/timetable`, form);
      setShowForm(false);
      setForm({ dayOfWeek: 'Monday', periodNumber: 1, subject: 'Math', teacher: '', room: '', timeSlot: '' });
      fetchTimetable();
    } catch {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/kids/${effectiveKidId}/timetable/${id}`);
      fetchTimetable();
    } catch {}
  };

  const handleCellClick = (day: string, period: number) => {
    const existing = getEntry(day, period);
    if (existing) return; // already filled — delete button handles it
    setForm(f => ({ ...f, dayOfWeek: day, periodNumber: period }));
    setShowForm(true);
  };

  return (
    <div>
      <main className="overflow-x-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-display font-extrabold text-gray-800">🗓️ Timetable</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-green-600 text-white text-sm px-3 py-1.5 rounded-lg"
          >+ Add</button>
        </div>
        {/* Add Form */}
        {showForm && (
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-5 mb-5">
            <h3 className="font-semibold text-gray-700 mb-3">Add Period</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Day</label>
                  <select value={form.dayOfWeek} onChange={e => setForm({ ...form, dayOfWeek: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400">
                    {DAYS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Period</label>
                  <select value={form.periodNumber} onChange={e => setForm({ ...form, periodNumber: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400">
                    {PERIODS.map(p => <option key={p} value={p}>Period {p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Subject</label>
                  <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400">
                    {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Time Slot</label>
                  <input type="text" value={form.timeSlot} onChange={e => setForm({ ...form, timeSlot: e.target.value })}
                    placeholder="e.g. 9:00-9:45"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Teacher</label>
                  <input type="text" value={form.teacher} onChange={e => setForm({ ...form, teacher: e.target.value })}
                    placeholder="Teacher name"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Room</label>
                  <input type="text" value={form.room} onChange={e => setForm({ ...form, room: e.target.value })}
                    placeholder="Room number"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-500 px-4 py-2">Cancel</button>
                <button type="submit" disabled={saving}
                  className="bg-green-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Weekly Grid */}
        <div className="min-w-[600px] max-w-4xl mx-auto">
          <table className="w-full border-collapse bg-white rounded-2xl shadow overflow-hidden">
            <thead>
              <tr className="bg-green-600 text-white">
                <th className="p-3 text-sm font-medium text-left w-16">Period</th>
                {DAYS.map(d => (
                  <th key={d} className="p-3 text-sm font-medium text-center">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERIODS.map(period => (
                <tr key={period} className="border-t border-gray-100">
                  <td className="p-2 text-center text-sm text-gray-500 font-medium bg-gray-50">{period}</td>
                  {DAYS.map(day => {
                    const entry = getEntry(day, period);
                    return (
                      <td
                        key={day}
                        onClick={() => handleCellClick(day, period)}
                        className="p-1.5 border-l border-gray-100 cursor-pointer hover:bg-gray-50 transition"
                      >
                        {entry ? (
                          <div className={`rounded-lg p-2 text-xs relative group ${SUBJECT_COLORS[entry.subject] || 'bg-gray-100 text-gray-700'}`}>
                            <p className="font-semibold">{entry.subject}</p>
                            {entry.timeSlot && <p className="text-gray-500">{entry.timeSlot}</p>}
                            {entry.teacher && <p className="truncate">{entry.teacher}</p>}
                            <button
                              onClick={ev => { ev.stopPropagation(); handleDelete(entry.id); }}
                              className="absolute top-1 right-1 hidden group-hover:block text-red-400 hover:text-red-600 leading-none"
                            >×</button>
                          </div>
                        ) : (
                          <div className="h-12 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center">
                            <span className="text-gray-300 text-lg">+</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default Timetable;
